import type { MenuItem } from 'primereact/menuitem'
import { useMsal } from '@azure/msal-react'
import { Button } from 'primereact/button'
import { PanelMenu } from 'primereact/panelmenu'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import styles from './MobileNavMenu.module.css'
import { signOutClickHandler } from './signOut'
import { mobileNavTexts, topNavTexts, userMenuTexts } from './texts'
import { getBookingSearchPath, getKnowledgeDatabaseEntryPath, knowledgeDatabaseEntries } from './topNavModel'

export function MobileNavMenu() {
  const navigate = useNavigate()
  const { instance } = useMsal()
  const activeAccount = instance.getActiveAccount()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) {
      return
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [open])

  const navigateAndClose = (path: string) => {
    setOpen(false)
    void navigate(path)
  }

  const navModel: MenuItem[] = [
    {
      label: topNavTexts.bookingSearch,
      command: () => navigateAndClose(getBookingSearchPath()),
    },
    {
      label: topNavTexts.knowledgeDatabase,
      items: knowledgeDatabaseEntries.map(entry => ({
        label: entry.label,
        command: () => navigateAndClose(getKnowledgeDatabaseEntryPath(entry)),
      })),
    },
  ]

  const userModel: MenuItem[] = activeAccount
    ? [
        {
          label: activeAccount.name ?? activeAccount.username,
          icon: 'pi pi-user',
          items: [
            {
              label: userMenuTexts.logout,
              icon: 'pi pi-sign-out',
              command: () => {
                setOpen(false)
                void signOutClickHandler(instance, activeAccount.homeAccountId)
              },
            },
          ],
        },
      ]
    : []

  return (
    <div
      ref={containerRef}
      className={styles.container}
      onKeyDownCapture={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault()
          event.stopPropagation()
          setOpen(false)
          event.currentTarget.querySelector<HTMLButtonElement>('button')?.focus()
        }
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false)
        }
      }}
    >
      <Button
        icon="pi pi-bars"
        text
        aria-label={mobileNavTexts.menu}
        aria-expanded={open}
        aria-haspopup
        onClick={() => setOpen(previous => !previous)}
        className={styles.toggle}
      />
      {open && (
        <div className={styles.panel}>
          <PanelMenu model={navModel} className={styles.menu} />
          {userModel.length > 0 && (
            <>
              <div className={styles.divider} />
              <PanelMenu model={userModel} className={styles.menu} />
            </>
          )}
        </div>
      )}
    </div>
  )
}
